import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";

import { EnquiryForm } from "@/components/enquiry-form";
import { CtaBand, FaqList, PageHero } from "@/components/page-parts";
import {
  FEES,
  FIRM,
  MICHAEL_PHONE,
  MICHAEL_PHONE_HREF,
  PENINSULA_ADDRESS,
  faqSchema,
  peninsulaBusinessSchema,
} from "@/lib/firm";

const DESCRIPTION = `Body corporate and unit owner advice for Scarborough, Redcliffe, Margate and Woody Point — by-laws, levies and recovery, committee decisions, disputes, records inspections and buying into a scheme. Solicitor-led, fixed fees agreed up front. Call Michael Klein on ${MICHAEL_PHONE}.`;

const FAQS = [
  {
    q: "Can a body corporate committee get legal advice without a general meeting?",
    a: "For most operational advice, yes. A committee can generally engage a solicitor within its spending limit and its authority to carry out the body corporate's functions; larger engagements, and litigation in particular, usually need a general meeting resolution. We tell you which category your issue falls into before any costs are incurred, because getting that wrong is itself a ground for challenging the decision.",
  },
  {
    q: "What can we do about an owner who will not pay levies?",
    a: "The body corporate must recover outstanding contributions, and under the Body Corporate and Community Management Act the recovery process is prescriptive: correctly struck levies, proper notice, penalty interest only if it has been resolved on, and proceedings started within the limitation period. Most failed recoveries we see fail on the paperwork, not the debt.",
  },
  {
    q: "A by-law is being ignored — pets, short-stay letting, parking, smoking. What now?",
    a: "First check the by-law is actually enforceable: by-laws that are oppressive, unreasonable, or that purport to prohibit something the Act protects cannot be enforced. If it stands, enforcement runs through a contravention notice and then adjudication in the Office of the Commissioner for Body Corporate and Community Management. Getting the notice wrong resets the process.",
  },
  {
    q: "Should I read the body corporate records before I buy a unit?",
    a: `Always, and before the contract becomes unconditional. The records show the sinking fund balance, past and proposed special levies, defect and building works history, litigation, management rights agreements and any by-law that would affect how you intend to live there. Our pre-signing contract review is ${FEES.contractReview.toLowerCase()} and we tell you plainly if the numbers do not support the price.`,
  },
  {
    q: "What are management rights and why do they matter to an owner?",
    a: "A caretaking and letting agreement gives a manager a long-term, often 10 or 25 year, contract paid from body corporate funds. The term, the remuneration review mechanism and the performance obligations set what the scheme pays for decades. Buyers of units in a scheme rarely read them, and committees inherit them.",
  },
  {
    q: "Do you act for owners against a body corporate as well?",
    a: "Yes, though not in the same scheme at the same time. Individual owners come to us over levy recovery, contravention notices, refused improvement applications, water ingress and lot boundary repair disputes.",
  },
];

const SERVICES = [
  {
    t: "Committees",
    d: "Advice on decisions and voting, committee spending limits, meeting and notice procedure, engaging contractors, and whether a resolution needs a general meeting. Practical answers in writing so the minutes record why the committee did what it did.",
  },
  {
    t: "By-laws",
    d: "Drafting and reviewing by-laws for pets, short-stay letting, parking, renovations and smoking, and testing whether an existing by-law is enforceable before you try to enforce it.",
  },
  {
    t: "Levies and recovery",
    d: "Striking contributions correctly, penalty interest, recovery notices and proceedings for unpaid levies, and defending owners who say the levy was not validly struck.",
  },
  {
    t: "Disputes and adjudication",
    d: "Conciliation and adjudication applications through the Commissioner's office, appeals to QCAT, and an honest assessment of whether the dispute is worth running.",
  },
  {
    t: "Building defects and water ingress",
    d: "Who is responsible for what between lot and common property, statutory warranty and QBCC timeframes, and preserving the scheme's position before the time limits pass.",
  },
  {
    t: "Management rights",
    d: "Reviewing, assigning, topping up and terminating caretaking and letting agreements — for committees, and for buyers and sellers of the rights themselves.",
  },
];

export const Route = createFileRoute("/body-corporate-scarborough")({
  head: () => ({
    meta: [
      { title: fitTitle(`Body Corporate Lawyers Scarborough & Redcliffe | ${FIRM.name}`) },
      { name: "description", content: fitDescription(DESCRIPTION) },
      {
        property: "og:title",
        content: fitTitle(`Body corporate and unit owner advice — Scarborough | ${FIRM.name}`),
      },
      { property: "og:description", content: fitDescription(DESCRIPTION) },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "/body-corporate-scarborough" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "/body-corporate-scarborough" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(peninsulaBusinessSchema) },
      { type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) },
    ],
  }),
  component: BodyCorporateScarborough,
});

function BodyCorporateScarborough() {
  return (
    <>
      <PageHero
        eyebrow="Scarborough & the Redcliffe Peninsula"
        title="Body corporate and unit owner advice, on the peninsula"
        lead={`The esplanade from Scarborough through Redcliffe and Margate is unit country, and most schemes here are run by volunteer committees without a lawyer on call. We advise committees and individual owners from our office at ${PENINSULA_ADDRESS} — fixed fees agreed before the work starts, and a straight answer about whether a fight is worth having.`}
      />

      <section className="mx-auto max-w-5xl px-5 py-16">
        <h2 className="font-serif text-3xl text-foreground">What we advise on</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {SERVICES.map((s) => (
            <div key={s.t} className="rounded-sm border border-border bg-card p-6">
              <h3 className="font-serif text-xl text-foreground">{s.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
            </div>
          ))}
        </div>

        <div className="mt-14">
          <h2 className="font-serif text-3xl text-foreground">Buying into a Scarborough scheme</h2>
          <div className="mt-5 space-y-5 text-base leading-relaxed text-muted-foreground">
            <p>
              A unit purchase is not a house purchase with fewer searches. Before the contract goes
              unconditional the body corporate records should be inspected and read properly: the
              sinking fund forecast against the age and condition of the building, any special levy
              already resolved on or foreshadowed, past water ingress and remediation, the
              caretaking and letting agreement, insurance, and the by-laws you will actually have to
              live under. Older esplanade buildings on the peninsula frequently carry deferred
              maintenance that shows up as a special levy within a year or two of settlement.
            </p>
            <p>
              Our pre-signing contract review is free, and residential purchases settle on our fixed
              fee of {FEES.purchase} inclusive of GST. If the contract ends under cooling off,
              finance or building and pest, no professional fee is charged.
            </p>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/conveyancing/$suburb"
              params={{ suburb: "scarborough" }}
              className="inline-flex rounded-sm border border-border bg-card px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:border-primary hover:text-primary"
            >
              Scarborough conveyancing
            </Link>
            <Link
              to="/scarborough"
              className="inline-flex rounded-sm border border-border bg-card px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:border-primary hover:text-primary"
            >
              Scarborough office
            </Link>
            <Link
              to="/scarborough-wills-and-estates"
              className="inline-flex rounded-sm border border-border bg-card px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:border-primary hover:text-primary"
            >
              Wills &amp; estates
            </Link>
          </div>
        </div>

        <div className="mt-14 rounded-sm border border-border bg-secondary/50 p-7">
          <h2 className="font-serif text-2xl text-foreground">
            Free information session for your building
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            We run a short, no-obligation session for committees and residents — half an hour on
            by-laws, levies and the decisions a committee can and cannot make on its own, then
            questions. No charge and no sales pitch. Ask for it on{" "}
            <a href={MICHAEL_PHONE_HREF} className="font-semibold text-primary underline">
              {MICHAEL_PHONE}
            </a>
            .
          </p>
        </div>

        <div className="mt-14">
          <h2 className="font-serif text-3xl text-foreground">Questions people ask</h2>
          <div className="mt-6">
            <FaqList items={FAQS} />
          </div>
        </div>

        <div className="mt-14 rounded-sm border border-border bg-card p-7">
          <EnquiryForm heading="Ask about a body corporate issue" />
        </div>
      </section>

      <CtaBand />
    </>
  );
}
